import { useState, useEffect, useMemo } from "react";
import { summarizeWarehouse } from "../utils/warehouse";

// BC stores some currencies under non-ISO codes (e.g. EURO, COLONES).
// Map them to ISO 4217 so Intl.NumberFormat doesn't throw.
const CURRENCY_ALIASES = { EURO: "EUR", COLONES: "CRC" };

const fmt = (n, currency = "CRC") => {
  if (n == null) return "—";
  const code = CURRENCY_ALIASES[currency] || currency || "CRC";
  try {
    return new Intl.NumberFormat("es-CR", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    const num = new Intl.NumberFormat("es-CR", { maximumFractionDigits: 0 }).format(n);
    return `${currency || ""} ${num}`.trim();
  }
};

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  // BC returns "0001-01-01" for empty date fields. Treat as null.
  if (isNaN(date.getTime()) || date.getFullYear() < 1900) return "—";
  return date.toLocaleDateString("es-CR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function LineRow({ line, currency, index, locationsById }) {
  const [expanded, setExpanded] = useState(false);
  const total = line.amountIncludingTax ?? line.amountExcludingTax;

  const location = line.locationId ? locationsById?.[line.locationId] : null;
  const locationLabel = location
    ? location.code || location.displayName || location.name
    : line.locationId
    ? "Asignado"
    : null;

  // BC's standard v2.0 purchase line schema doesn't expose project/task
  // fields. They may show up under common Spanish/English names if a
  // localization or extension surfaces them — try a few.
  const projectNo = line.jobNo || line.projectNo || line.dimensionProyecto;
  const taskNo = line.jobTaskNo || line.projectTaskNo || line.dimensionTarea;
  const expectedDate = line.expectedReceiptDate;

  // BC's `unitCost` field reads from the item card (often 0). Use the actual
  // line price: amountExcludingTax / quantity.
  const computedUnitCost =
    line.quantity && line.amountExcludingTax != null
      ? line.amountExcludingTax / line.quantity
      : null;

  return (
    <div className={`line-card ${expanded ? "expanded" : ""}`}>
      <button
        type="button"
        className="line-card-head"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        <div className="line-card-desc">
          <span className="line-card-idx">{index + 1}</span>
          <span>{line.description || line.itemId || "—"}</span>
        </div>
        <div className="line-card-meta">
          <span className="line-card-qty">{line.quantity}</span>
          <span className="line-card-total">{fmt(total, currency)}</span>
          <span className="line-card-chev">{expanded ? "▾" : "▸"}</span>
        </div>
      </button>
      {expanded && (
        <div className="line-card-body">
          <div className="line-extra-row">
            <span>Almacén</span>
            <span className={locationLabel ? "tag tag-ok" : "tag tag-warn"}>
              {locationLabel ? `✓ ${locationLabel}` : "✗ Sin asignar"}
            </span>
          </div>
          <div className="line-extra-row">
            <span>Proyecto</span>
            <span className={projectNo ? "tag tag-ok" : "tag tag-warn"}>
              {projectNo || "✗ Sin proyecto"}
            </span>
          </div>
          {projectNo && (
            <div className="line-extra-row">
              <span>Tarea</span>
              <span className={taskNo ? "tag tag-ok" : "tag tag-warn"}>
                {taskNo || "✗ Sin tarea"}
              </span>
            </div>
          )}
          <div className="line-extra-row">
            <span>Precio unitario</span>
            <span>{fmt(computedUnitCost, currency)}</span>
          </div>
          {expectedDate && (
            <div className="line-extra-row">
              <span>Recepción esperada</span>
              <span>{fmtDate(expectedDate)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function OrderDetail({ order, onBack, onApprove, onReject, actionLoading, fetchLines, locationsById }) {
  const [lines, setLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [errorModal, setErrorModal] = useState(null);

  const isLoading = actionLoading === order.number;
  const warehouse = useMemo(
    () => summarizeWarehouse(lines, locationsById),
    [lines, locationsById]
  );

  useEffect(() => {
    setLinesLoading(true);
    fetchLines(order.id)
      .then(setLines)
      .catch(() => setLines([]))
      .finally(() => setLinesLoading(false));
  }, [order.id, fetchLines]);

  const showToast = (msg, type) => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleApprove = async () => {
    const result = await onApprove(order.number);
    if (result.success) {
      showToast("✓ Pedido aprobado", "success");
      setTimeout(onBack, 1200);
    } else {
      setErrorModal({ title: "No se pudo aprobar el pedido", message: result.error });
    }
  };

  const handleReject = async () => {
    setShowRejectModal(false);
    const result = await onReject(order.number);
    if (result.success) {
      showToast("Pedido rechazado", "success");
      setTimeout(onBack, 1200);
    } else {
      setErrorModal({ title: "No se pudo rechazar el pedido", message: result.error });
    }
  };

  const currency = order.currencyCode || "CRC";

  return (
    <div className="detail-view">
      <div className="detail-header">
        <button className="btn-back" onClick={onBack}>←</button>
        <div className="detail-header-info">
          <div className="detail-order-num">{order.number}</div>
          <div className="detail-vendor-name">{order.vendorName || "Proveedor"}</div>
          {warehouse && (
            <span className={`wh-chip wh-chip-${warehouse.kind}`}>
              {warehouse.label}
            </span>
          )}
        </div>
      </div>

      <div className="detail-body">
        {/* Amounts */}
        <div className="detail-section">
          <div className="detail-section-title">Montos</div>
          <div className="detail-grid">
            <div className="detail-field full">
              <div className="field-label">Total del Pedido</div>
              <div className="field-value amount">
                {fmt(order.totalAmountIncludingTax, currency)}
              </div>
            </div>
            <div className="detail-field">
              <div className="field-label">Subtotal</div>
              <div className="field-value">{fmt(order.totalAmountExcludingTax, currency)}</div>
            </div>
            <div className="detail-field">
              <div className="field-label">Impuesto</div>
              <div className="field-value">{fmt(order.totalTaxAmount, currency)}</div>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="detail-section">
          <div className="detail-section-title">Fechas</div>
          <div className="detail-grid">
            <div className="detail-field">
              <div className="field-label">Fecha Pedido</div>
              <div className="field-value">{fmtDate(order.orderDate)}</div>
            </div>
            <div className="detail-field">
              <div className="field-label">Entrega Esperada</div>
              <div className="field-value">{fmtDate(order.requestedReceiptDate)}</div>
            </div>
          </div>
        </div>

        {/* Vendor info */}
        <div className="detail-section">
          <div className="detail-section-title">Proveedor</div>
          <div className="detail-grid">
            <div className="detail-field full">
              <div className="field-label">Nombre</div>
              <div className="field-value">{order.vendorName || "—"}</div>
            </div>
          </div>
        </div>

        {/* Lines */}
        <div className="detail-section">
          <div className="detail-section-title">
            Líneas ({linesLoading ? "..." : lines.length})
          </div>
          {linesLoading ? (
            <div className="loading-state" style={{ padding: "2rem 0" }}>
              <div className="spinner" style={{ width: 24, height: 24, borderWidth: 2 }} />
            </div>
          ) : lines.length === 0 ? (
            <div className="empty-lines">Sin líneas</div>
          ) : (
            <div className="line-list">
              {lines.map((line, i) => (
                <LineRow
                  key={line.id || i}
                  line={line}
                  currency={currency}
                  index={i}
                  locationsById={locationsById}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="action-bar">
        <button
          className="btn-reject"
          onClick={() => setShowRejectModal(true)}
          disabled={isLoading}
        >
          Rechazar
        </button>
        <button
          className="btn-approve"
          onClick={handleApprove}
          disabled={isLoading}
        >
          {isLoading ? "Procesando..." : "✓ Aprobar"}
        </button>
      </div>

      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">¿Rechazar este pedido?</div>
            <div className="modal-sub">{order.number} · {order.vendorName}</div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowRejectModal(false)}>
                Cancelar
              </button>
              <button className="btn-confirm-reject" onClick={handleReject}>
                Confirmar rechazo
              </button>
            </div>
          </div>
        </div>
      )}

      {errorModal && (
        <div className="modal-overlay" onClick={() => setErrorModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">⚠ {errorModal.title}</div>
            <div className="error-message">{errorModal.message}</div>
            <div className="modal-actions">
              <button className="btn-confirm-reject" onClick={() => setErrorModal(null)}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast ${toast.type}`}>{toast.msg}</div>
      )}
    </div>
  );
}
